import { useCallback, useEffect, useRef, useState } from 'react';
import { SectionDomId } from './productForm';

// Depois de um clique na navegação, o listener fica mudo por este tempo: a
// rolagem suave passa por outras seções e a barra ativa piscaria no caminho.
const CLICK_LOCK_MS = 600;
// Folga (px) abaixo do cabeçalho fixo para considerar uma seção "no topo".
const ACTIVE_SLACK_PX = 40;

/**
 * Scroll-spy da navegação lateral do editor (algoritmo do protótipo, adaptado à
 * rolagem da JANELA — fora de /operacoes o Layout não tem contêiner rolável).
 *
 * A seção ativa é a última `#secao-<id>` cujo topo já passou do cabeçalho
 * (`offsetPx`); no fim da página, é a última. `scrollToSection(id)` rola suave
 * (o `scroll-mt-*` da seção compensa o cabeçalho) e fixa o ativo durante a
 * animação. A lista de ids pode mudar (a seção Composição só existe em kit).
 */
export function useSectionSpy(sectionIds, { offsetPx = 96 } = {}) {
    const idsKey = sectionIds.join('|');
    const [activeId, setActiveId] = useState(sectionIds[0] ?? null);
    const lockUntilRef = useRef(0);

    useEffect(() => {
        const ids = idsKey ? idsKey.split('|') : [];
        if (ids.length === 0 || typeof window === 'undefined') {
            return undefined;
        }
        let frame = 0;
        let unlockTimer = 0;

        const compute = () => {
            frame = 0;
            if (Date.now() < lockUntilRef.current) {
                return;
            }
            let current = ids[0];
            for (const id of ids) {
                const element = document.getElementById(SectionDomId(id));
                if (element && element.getBoundingClientRect().top - offsetPx <= ACTIVE_SLACK_PX) {
                    current = id;
                }
            }
            const root = document.documentElement;
            if (window.innerHeight + window.scrollY >= root.scrollHeight - 4) {
                current = ids[ids.length - 1];
            }
            setActiveId((previous) => (previous === current ? previous : current));
        };

        const schedule = () => {
            if (!frame) {
                frame = window.requestAnimationFrame(compute);
            }
            // Recalcula quando a trava do clique acabar (a rolagem pode ter
            // parado noutra seção, ex.: a última não chega ao topo).
            window.clearTimeout(unlockTimer);
            unlockTimer = window.setTimeout(compute, CLICK_LOCK_MS + 50);
        };

        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        compute();
        return () => {
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
            if (frame) {
                window.cancelAnimationFrame(frame);
            }
            window.clearTimeout(unlockTimer);
        };
    }, [idsKey, offsetPx]);

    const scrollToSection = useCallback((id) => {
        const element = document.getElementById(SectionDomId(id));
        if (!element) {
            return;
        }
        lockUntilRef.current = Date.now() + CLICK_LOCK_MS;
        setActiveId(id);
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    return { activeId, scrollToSection };
}

export default useSectionSpy;
